import React, { useCallback, useEffect, useMemo } from 'react';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Tenders from './components/Tenders';
import PurchaseOrders from './components/PurchaseOrders';
import Inventory from './components/Inventory';
import Shipments from './components/Shipments';
import Suppliers from './components/Suppliers';
import Reports from './components/Reports';
import Administration from './components/Administration';
import Profile from './components/Profile';
import ApiDocumentation from './components/ApiDocumentation';
import PurchaseRequisitions from './components/PurchaseRequisitions';
import BudgetOverview from './components/BudgetOverview';
import Approvals from './components/Approvals';
import ProjectStatus from './components/ProjectStatus';
import ProjectsHome from './components/projects/ProjectsHome';
import ProjectTimeline from './components/projects/ProjectTimeline';
import ProjectRisks from './components/projects/ProjectRisks';
import ProjectMilestones from './components/projects/ProjectMilestones';
import DevelopmentPortfolio from './components/renewables/DevelopmentPortfolio';
import DevelopmentGeoReference from './components/renewables/DevelopmentGeoReference';
import DevelopmentPermits from './components/renewables/DevelopmentPermits';
import DevelopmentRegulatoryAssistant from './components/renewables/DevelopmentRegulatoryAssistant';
import DevelopmentPermitAlerts from './components/renewables/DevelopmentPermitAlerts';
import DevelopmentReporting from './components/renewables/DevelopmentReporting';
import DevelopmentTeam from './components/renewables/DevelopmentTeam';
import SuppliersOverview from './components/suppliers/SuppliersOverview';
import SuppliersLegalAdmin from './components/suppliers/SuppliersLegalAdmin';
import SuppliersComplianceMatrix from './components/suppliers/SuppliersComplianceMatrix';
import SuppliersWorkHistory from './components/suppliers/SuppliersWorkHistory';
import SuppliersOrdersBilling from './components/suppliers/SuppliersOrdersBilling';
// import TerrainProfiles from './components/TerrainProfiles';
import LegalArea from './components/LegalArea';
import ConstructionSchedule from './components/construction/ConstructionSchedule';
import ConstructionProgress from './components/construction/ConstructionProgress';
import ConstructionCosts from './components/construction/ConstructionCosts';
import ConstructionDailyLog from './components/construction/ConstructionDailyLog';
import ConstructionMaterials from './components/construction/ConstructionMaterials';
import ConstructionContracts from './components/construction/ConstructionContracts';
import ConstructionQuality from './components/construction/ConstructionQuality';
import ModuleDisabled from './components/common/ModuleDisabled';
import { useAppContext } from './context/AppContext';
import { useModuleSettings } from './context/ModuleSettingsContext';
import { canViewConstructionPage } from './construction/permissions';

const CONSTRUCTION_PAGES = [
  'construction-schedule',
  'construction-progress',
  'construction-costs',
  'construction-daily',
  'construction-materials',
  'construction-contracts',
  'construction-quality'
];

const CONSTRUCTION_COMPONENTS = {
  'construction-schedule': ConstructionSchedule,
  'construction-progress': ConstructionProgress,
  'construction-costs': ConstructionCosts,
  'construction-daily': ConstructionDailyLog,
  'construction-materials': ConstructionMaterials,
  'construction-contracts': ConstructionContracts,
  'construction-quality': ConstructionQuality
};

const App = () => {
  const { currentUser, activePage, setActivePage } = useAppContext();
  const { isPageEnabled } = useModuleSettings();
  const profileNavigationMap = useMemo(
    () => ({
      operativo: [
        'dashboard',
        'procurement-requisitions',
        'procurement-orders',
        'procurement-shipments',
        ...CONSTRUCTION_PAGES
      ],
      gerente: [
        'dashboard',
        'projects',
        'projects-budget',
        'projects-timeline',
        'projects-risks',
        'projects-milestones',
        'projects-status',
        'development-portfolio',
        'development-geo',
        'development-permits',
        'development-assistant',
        'development-alerts',
        'development-reporting',
        'development-team',
        'suppliers-overview',
        'suppliers-legal',
        'suppliers-compliance',
        'suppliers-history',
        'suppliers-orders',
        'procurement-requisitions',
        'procurement-tenders',
        'procurement-orders',
        'procurement-approvals',
        'procurement-inventory',
        'procurement-shipments',
        'procurement-suppliers',
        'reports',
        ...CONSTRUCTION_PAGES
      ],
      director: [
        'dashboard',
        'projects',
        'projects-timeline',
        'projects-risks',
        'projects-status',
        'projects-budget',
        'development-portfolio',
        'development-permits',
        'development-alerts',
        'development-reporting',
        'suppliers-overview',
        'suppliers-compliance',
        'suppliers-orders',
        'procurement-requisitions',
        'procurement-tenders',
        'procurement-orders',
        'procurement-approvals',
        'procurement-inventory',
        'procurement-suppliers',
        'reports',
        'admin',
        ...CONSTRUCTION_PAGES
      ],
      super_user: [
        'dashboard',
        'projects',
        'projects-budget',
        'projects-timeline',
        'projects-risks',
        'projects-milestones',
        'projects-status',
        'development-portfolio',
        'development-geo',
        'development-permits',
        'development-assistant',
        'development-alerts',
        'development-reporting',
        'development-team',
        'suppliers-overview',
        'suppliers-legal',
        'suppliers-compliance',
        'suppliers-history',
        'suppliers-orders',
        'procurement-requisitions',
        'procurement-tenders',
        'procurement-orders',
        'procurement-approvals',
        'procurement-inventory',
        'procurement-shipments',
        'procurement-suppliers',
        'reports',
        'admin',
        'api',
        ...CONSTRUCTION_PAGES
      ]
    }),
    []
  );

  const allowedPagesList = useMemo(() => {
    if (!currentUser) return null;
    if (Array.isArray(currentUser.allowedPages) && currentUser.allowedPages.length > 0) {
      return currentUser.allowedPages;
    }
    const profile = currentUser.profile || 'operativo';
    return profileNavigationMap[profile] || profileNavigationMap.operativo;
  }, [currentUser, profileNavigationMap]);

  const allowedPagesSet = useMemo(() => {
    if (!allowedPagesList) return null;
    return new Set(allowedPagesList);
  }, [allowedPagesList]);

  const isPageAllowed = useCallback(
    (page) => {
      if (page === 'profile' || page === 'login') return true;
      if (!isPageEnabled(page)) return false;
      if (CONSTRUCTION_PAGES.includes(page) && !canViewConstructionPage(currentUser?.role, page)) return false;
      if (!allowedPagesSet) return true;
      return allowedPagesSet.has(page);
    },
    [allowedPagesSet, isPageEnabled, currentUser?.role]
  );

  const effectiveAllowedPages = useMemo(() => {
    if (!allowedPagesSet) return null;
    return new Set([...allowedPagesSet].filter((page) => isPageAllowed(page)));
  }, [allowedPagesSet, isPageAllowed]);

  useEffect(() => {
    if (!currentUser) return;
    if (!isPageAllowed(activePage)) {
      const fallback = allowedPagesList ? allowedPagesList[0] : 'dashboard';
      setActivePage(fallback || 'dashboard');
    }
  }, [activePage, currentUser, allowedPagesList, isPageAllowed, setActivePage]);

  if (!currentUser) {
    return <Login />;
  }

  const renderCurrentPage = () => {
    if (!isPageEnabled(activePage)) return <ModuleDisabled pageId={activePage} />;
    if (CONSTRUCTION_COMPONENTS[activePage]) {
      const Page = CONSTRUCTION_COMPONENTS[activePage];
      return isPageAllowed(activePage) ? <Page /> : <Dashboard />;
    }
    switch (activePage) {
      case 'dashboard':
        return <Dashboard />;
      case 'projects':
        return isPageAllowed('projects') ? <ProjectsHome /> : <Dashboard />;
      case 'projects-budget':
        return isPageAllowed('projects-budget') ? <BudgetOverview /> : <Dashboard />;
      case 'projects-timeline':
        return isPageAllowed('projects-timeline') ? <ProjectTimeline /> : <Dashboard />;
      case 'projects-risks':
        return isPageAllowed('projects-risks') ? <ProjectRisks /> : <Dashboard />;
      case 'projects-milestones':
        return isPageAllowed('projects-milestones') ? <ProjectMilestones /> : <Dashboard />;
      case 'projects-status':
        return isPageAllowed('projects-status') ? <ProjectStatus /> : <Dashboard />;
      case 'development-portfolio':
        return isPageAllowed('development-portfolio') ? <DevelopmentPortfolio /> : <Dashboard />;
      case 'development-geo':
        return isPageAllowed('development-geo') ? <DevelopmentGeoReference /> : <Dashboard />;
      case 'development-permits':
        return isPageAllowed('development-permits') ? <DevelopmentPermits /> : <Dashboard />;
      case 'development-assistant':
        return isPageAllowed('development-assistant') ? <DevelopmentRegulatoryAssistant /> : <Dashboard />;
      case 'development-alerts':
        return isPageAllowed('development-alerts') ? <DevelopmentPermitAlerts /> : <Dashboard />;
      case 'development-reporting':
        return isPageAllowed('development-reporting') ? <DevelopmentReporting /> : <Dashboard />;
      case 'development-team':
        return isPageAllowed('development-team') ? <DevelopmentTeam /> : <Dashboard />;
      case 'suppliers-overview':
        return isPageAllowed('suppliers-overview') ? <SuppliersOverview /> : <Dashboard />;
      case 'suppliers-legal':
        return isPageAllowed('suppliers-legal') ? <SuppliersLegalAdmin /> : <Dashboard />;
      case 'suppliers-compliance':
        return isPageAllowed('suppliers-compliance') ? <SuppliersComplianceMatrix /> : <Dashboard />;
      case 'suppliers-history':
        return isPageAllowed('suppliers-history') ? <SuppliersWorkHistory /> : <Dashboard />;
      case 'suppliers-orders':
        return isPageAllowed('suppliers-orders') ? <SuppliersOrdersBilling /> : <Dashboard />;
      case 'procurement-requisitions':
        return isPageAllowed('procurement-requisitions') ? <PurchaseRequisitions /> : <Dashboard />;
      case 'procurement-tenders':
        return isPageAllowed('procurement-tenders') ? <Tenders /> : <Dashboard />;
      case 'procurement-orders':
        return isPageAllowed('procurement-orders') ? <PurchaseOrders /> : <Dashboard />;
      case 'procurement-approvals':
        return isPageAllowed('procurement-approvals') ? <Approvals /> : <Dashboard />;
      case 'procurement-inventory':
        return isPageAllowed('procurement-inventory') ? <Inventory /> : <Dashboard />;
      case 'procurement-shipments':
        return isPageAllowed('procurement-shipments') ? <Shipments /> : <Dashboard />;
      case 'procurement-suppliers':
        return isPageAllowed('procurement-suppliers') ? <Suppliers /> : <Dashboard />;
      case 'reports':
        return isPageAllowed('reports') ? <Reports /> : <Dashboard />;
      case 'approvals':
        return ['director', 'super_user'].includes(currentUser.profile)
          ? <Approvals />
          : <Dashboard />;
      case 'api':
        return isPageAllowed('api') ? <ApiDocumentation /> : <Dashboard />;
      case 'profile':
        return <Profile />;
      case 'admin':
        return isPageAllowed('admin') &&
          (currentUser.profile === 'director' || currentUser.profile === 'super_user')
          ? <Administration />
          : <Dashboard />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <Layout
      currentUser={currentUser}
      currentPage={activePage}
      onNavigate={setActivePage}
      allowedPages={effectiveAllowedPages}
    >
      {renderCurrentPage()}
    </Layout>
  );
};

export default App;
